import {
  memo,
  useEffect,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import styled from "styled-components";

import {
  resolvePlaybackFrameClock,
  useVideoFrameTime,
  useVideoTimeline,
  type PlaybackTransport,
} from "../Video";
import { controlFocusRing } from "../foundations/controlStyles";
import {
  findMediaTimelinePointsAtTime,
  formatMediaPlaybackTime,
  MEDIA_TIMELINE_POLICY,
  mediaTimelinePosition,
  normalizeMediaTimelineCurrent,
} from "./mediaTimelineMath";
import type { NormalizedMarker, TimelineDragTarget } from "./timelineTypes";
import { useMediaTimelineShortcuts } from "./useMediaTimelineShortcuts";

const TIMELINE_DURATION_EPSILON_SECONDS = MEDIA_TIMELINE_POLICY.durationComparisonEpsilonSeconds;

export type TimelinePointerHandler = (event: ReactPointerEvent<HTMLElement>) => void;

export const TickRegion = styled.div`
  position: relative;
`;

export const TickCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: 1.4rem;
`;

export const TimelineTrack = styled.div<{
  $disabled: boolean;
  $dragTarget: TimelineDragTarget;
  $focusVisible: boolean;
}>`
  position: relative;
  height: 2.2rem;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surface};
  /* Drags show their own cursor first: a pan can run while seeking (and so $disabled) is off. */
  cursor: ${({ $disabled, $dragTarget }) =>
    $dragTarget === "selectionStart" || $dragTarget === "selectionEnd"
      ? "ew-resize"
      : $dragTarget === "pan"
        ? "grabbing"
        : $disabled
          ? "default"
          : "pointer"};
  /* Disabled track must not swallow native touch scrolling — the pointer handlers already bail. */
  touch-action: ${({ $disabled }) => ($disabled ? "auto" : "none")};

  ${({ $focusVisible }) => $focusVisible && controlFocusRing("2px")}
`;

const TimelineRangeInput = styled.input.attrs({ type: "range" })`
  position: absolute;
  z-index: 4;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  pointer-events: none;

  &:focus {
    outline: none;
  }
`;

export const DurationRegion = styled.div`
  position: absolute;
  z-index: 0;
  top: 0;
  right: calc(100% - var(--c6-media-duration-end));
  left: var(--c6-media-duration-start);
  height: 100%;
  background: repeating-linear-gradient(
    135deg,
    ${({ theme }) => theme.color.border.subtle},
    ${({ theme }) => theme.color.border.subtle} 1px,
    transparent 1px,
    transparent 6px
  );
  pointer-events: none;
`;

export const SeekableRegion = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  right: calc(100% - var(--c6-media-seekable-end));
  left: var(--c6-media-seekable-start);
  height: 100%;
  border-inline: 1px solid ${({ theme }) => theme.color.border.selected};
  background: ${({ theme }) => theme.color.bg.selected};
  pointer-events: none;
`;

export const LiveEdgeIndicator = styled.span`
  position: absolute;
  z-index: 2;
  top: 0;
  left: var(--c6-media-seekable-end);
  width: 2px;
  height: 100%;
  border-radius: 999px;
  background: ${({ theme }) => theme.color.border.selected};
  transform: translateX(-50%);
  pointer-events: none;
`;

/* Topmost in the track — above the selection handles (z 7/8) and the viewport-wide navigation
   layer (z 2). The current position is the primary readout, and being pointer-transparent it
   hides nothing.
   PlayheadLine과 PlayheadHandle은 하나의 playhead이며, 에디터의 빨간 재생선 관례를 나타내기
   위해 status.recording을 함께 참조한다. */
const PlayheadLine = styled.span`
  position: absolute;
  z-index: 9;
  top: 0;
  left: var(--c6-media-playhead-position);
  width: 2px;
  height: 100%;
  border-radius: 999px;
  background: ${({ theme }) => theme.color.status.recording};
  transform: translateX(-50%);
  pointer-events: none;
`;

const PlayheadHandle = styled.span<{ $disabled: boolean }>`
  position: absolute;
  z-index: 5;
  bottom: 0;
  left: var(--c6-media-playhead-position);
  display: inline-flex;
  width: 1rem;
  height: 1rem;
  align-items: center;
  justify-content: center;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "grab")};
  transform: translate(-50%, 50%);
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  /* Not a TimelineTrack descendant, so the track's touch-action does not cover it — without its
     own opt-out a touch drag starts native scrolling and the browser cancels the pointer stream. */
  touch-action: none;

  &::before {
    width: 0.625rem;
    height: 0.625rem;
    border-radius: 20%;
    background: ${({ theme }) => theme.color.status.recording};
    box-shadow: ${({ theme }) => theme.elevation.shadow.level[1]};
    content: "";
    transform: rotate(45deg);
  }
`;

interface TimelinePlayheadCursorProps {
  transport: PlaybackTransport;
  timelineRef: RefObject<HTMLDivElement | null>;
  durationSeconds: number;
  viewportStart: number;
  viewportDuration: number;
  dragTarget: TimelineDragTarget;
  edgePanning: boolean;
  disabled: boolean;
  /** The playhead left the window; the viewport centres on this time. */
  onFollowTime: (timeSeconds: number) => void;
  onPointerDown: TimelinePointerHandler;
  onPointerMove: TimelinePointerHandler;
  onPointerUp: TimelinePointerHandler;
}

interface TimelinePlayheadLineProps {
  transport: PlaybackTransport;
  durationSeconds: number;
  viewportStart: number;
  viewportDuration: number;
}

/** A row-owned playhead line that still reads the viewport's shared time coordinate. */
export const TimelinePlayheadLine = memo(function TimelinePlayheadLine({
  transport,
  durationSeconds,
  viewportStart,
  viewportDuration,
}: TimelinePlayheadLineProps) {
  const lineRef = useRef<HTMLSpanElement>(null);
  // The line follows the frame clock through style alone; React never re-renders it per frame.
  const applyTime = useCallback(
    (seconds: number) => {
      const line = lineRef.current;
      if (!line) return;
      const current = normalizeMediaTimelineCurrent(seconds, durationSeconds);
      const visible = current >= viewportStart && current <= viewportStart + viewportDuration;
      line.style.display = visible ? "" : "none";
    },
    [durationSeconds, viewportDuration, viewportStart],
  );
  useLayoutEffect(() => {
    applyTime(resolvePlaybackFrameClock(transport).getTimeSeconds());
  }, [applyTime, transport]);
  useVideoFrameTime(transport, applyTime);
  return (
    <PlayheadLine
      ref={lineRef}
      aria-hidden="true"
      data-media-timeline-playhead-line="true"
      style={{ display: "none" }}
    />
  );
});

export const TimelinePlayheadCursor = memo(function TimelinePlayheadCursor({
  transport,
  timelineRef,
  durationSeconds,
  viewportStart,
  viewportDuration,
  dragTarget,
  edgePanning,
  disabled,
  onFollowTime,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: TimelinePlayheadCursorProps) {
  // The snapshot drives viewport recentering and the handle; the shared position variable follows
  // the frame clock so every row's line moves per frame without a React commit.
  const currentTimeSeconds = useVideoTimeline(transport, (snapshot) => snapshot.currentTimeSeconds);
  const current = normalizeMediaTimelineCurrent(currentTimeSeconds, durationSeconds);
  const previousTimeRef = useRef(0);
  const visibleEnd = viewportStart + viewportDuration;
  const applyPosition = useCallback(
    (seconds: number) => {
      timelineRef.current?.style.setProperty(
        "--c6-media-playhead-position",
        mediaTimelinePosition(
          normalizeMediaTimelineCurrent(seconds, durationSeconds),
          viewportStart,
          viewportDuration,
        ),
      );
    },
    [durationSeconds, timelineRef, viewportDuration, viewportStart],
  );
  useLayoutEffect(() => {
    applyPosition(resolvePlaybackFrameClock(transport).getTimeSeconds());
  }, [applyPosition, transport]);
  useVideoFrameTime(transport, applyPosition);

  useEffect(() => {
    const delta = Math.abs(current - previousTimeRef.current);
    previousTimeRef.current = current;
    // Held edge panning is a manual viewport intervention like dragging: without suppression,
    // every playback telemetry tick recenters the viewport the pan loop just moved away.
    if (dragTarget !== null || edgePanning || delta === 0) return;
    if (current < viewportStart || current > visibleEnd) onFollowTime(current);
  }, [current, dragTarget, edgePanning, onFollowTime, viewportStart, visibleEnd]);

  if (current < viewportStart || current > visibleEnd) return null;

  return (
    <PlayheadHandle
      $disabled={disabled}
      data-media-playhead-cursor="true"
      aria-hidden="true"
      onPointerDown={(event) => {
        event.stopPropagation();
        onPointerDown(event);
      }}
      onPointerMove={(event) => {
        event.stopPropagation();
        onPointerMove(event);
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        onPointerUp(event);
      }}
      onPointerCancel={(event) => {
        event.stopPropagation();
        onPointerUp(event);
      }}
      onLostPointerCapture={(event) => {
        event.stopPropagation();
        onPointerUp(event);
      }}
      // Not a TimelineTrack descendant, so the track's suppression does not cover it — on
      // macOS a Ctrl+primary click starts the pan drag AND synthesizes a context menu that
      // would interrupt the pointer stream.
      onContextMenu={(event) => event.preventDefault()}
    />
  );
});

interface TimelineTrackTelemetryProps {
  transport: PlaybackTransport;
  durationSeconds: number;
  stepSeconds: number;
  disabled: boolean;
  seekRangeStart: number;
  seekRangeEnd: number;
  seekLabel: string;
  inputRef: RefObject<HTMLInputElement | null>;
  points: readonly NormalizedMarker[];
  canRemoveMarker: (trackId: string) => boolean;
  onFocus: () => void;
  onBlur: () => void;
  /** Fired for every slider keydown so the owner can restore keyboard focus-visible modality. */
  onKeyboardInteraction: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  onSeek: (seconds: number) => void;
  onMarkerCreate?: (timeSeconds: number) => void;
  onMarkerRemove?: (markerId: string, trackId: string) => void;
}

export const TimelineTrackTelemetry = memo(function TimelineTrackTelemetry({
  transport,
  durationSeconds,
  stepSeconds,
  disabled,
  seekRangeStart,
  seekRangeEnd,
  seekLabel,
  inputRef,
  points,
  canRemoveMarker,
  onFocus,
  onBlur,
  onKeyboardInteraction,
  onSeek,
  onMarkerCreate,
  onMarkerRemove,
}: TimelineTrackTelemetryProps) {
  const currentTimeSeconds = useVideoTimeline(transport, (snapshot) => snapshot.currentTimeSeconds);
  const current = normalizeMediaTimelineCurrent(currentTimeSeconds, durationSeconds);
  // Removal never guesses: a marker pinned alone at the playhead (the same epsilon the nearest
  // control uses for "already there") is the target. A same-time tie resolves only through the
  // one marker the consumer marked `active` — the selection a cluster popup pick just made —
  // and otherwise stays ambiguous, so Delete passes through.
  const removableMarker = useMemo(() => {
    if (onMarkerRemove === undefined) return null;
    const atPlayhead = findMediaTimelinePointsAtTime(
      points,
      current,
      TIMELINE_DURATION_EPSILON_SECONDS,
    );
    const candidates =
      atPlayhead.length === 1
        ? atPlayhead
        : atPlayhead.filter(({ marker }) => marker.active === true);
    const target = candidates.length === 1 ? candidates[0] : undefined;
    // Capability checks must not change the tie or substitute a different marker for its target.
    return target && canRemoveMarker(target.trackId) ? target : null;
  }, [canRemoveMarker, current, onMarkerRemove, points]);
  // The slider runs the same public dispatcher a consumer can attach to a wider scope; consumed
  // events stop propagation there, so the two layers never double-fire one keystroke.
  const shortcuts = useMediaTimelineShortcuts({
    transport,
    stepSeconds,
    disabled,
    onMarkerCreate,
    onMarkerRemove:
      removableMarker === null
        ? undefined
        : (markerId) => onMarkerRemove?.(markerId, removableMarker.trackId),
    removableMarkerId: removableMarker?.marker.id ?? null,
  });

  return (
    <>
      <TimelineRangeInput
        ref={inputRef}
        data-media-timeline-input=""
        aria-label={seekLabel}
        aria-keyshortcuts={shortcuts.ariaKeyShortcuts}
        aria-valuetext={formatMediaPlaybackTime(current, durationSeconds)}
        min={seekRangeStart}
        max={durationSeconds > 0 ? Math.max(seekRangeStart, seekRangeEnd) : 1}
        step={stepSeconds}
        value={Math.max(seekRangeStart, Math.min(seekRangeEnd, current))}
        disabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={(event) => {
          onKeyboardInteraction(event);
          shortcuts.onKeyDown(event);
        }}
        onChange={(event) => onSeek(Number(event.currentTarget.value))}
      />
    </>
  );
});
